import { useAuthContext } from "@features/auth/contexts/AuthContext";
import {
	AUTH_TOKEN_FETCH_ERROR_TITLE,
	COPILOT_URL,
	RedeemCopilotChat,
	RedeemCopilotContext,
} from "@features/redeem-copilot";
import { COPILOT_INSTRUCTIONS } from "@features/redeem-copilot/instructions";
import { getAuthToken } from "@helpers/auth-provider";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import { LANGUAGE_NAMES, type Locale } from "@memorang/constants/i18n";
import Loader from "@memorang/ui/components/Loader";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RedeemCodeRouter from "../RedeemCodeRouter";

const RedeemCodeCopilotWrapper = () => {
	const tenantReadableName = useAppStore((store) => store.tenant.name);
	const appName = useAppStore((store) => store.app.name);
	const appSupportEmail = useAppStore((store) => store.supportEmail);

	const { email, username, userLogicalId, isLoading } = useAuthContext();
	const { t, i18n } = useTranslation();

	const [authHeaders, setAuthHeaders] = useState<Record<string, string>>();
	const [isTokenResolved, setIsTokenResolved] = useState(false);

	useEffect(() => {
		if (isLoading) {
			// Reset so a signed-out flash that resolved the token cannot leave the
			// redeem router mounted once the hub user query starts for a session.
			setIsTokenResolved(false);
			setAuthHeaders(undefined);
			return;
		}

		if (!userLogicalId) {
			setIsTokenResolved(true);
			return;
		}

		getAuthToken()
			.then((token) => {
				if (token) {
					setAuthHeaders({ Authorization: `Bearer ${token}` });
				}
			})
			.catch((error) => {
				captureErrorToSentry(
					error,
					{ userLogicalId },
					AUTH_TOKEN_FETCH_ERROR_TITLE,
				);
			})
			.finally(() => {
				setIsTokenResolved(true);
			});
	}, [isLoading, userLogicalId]);

	const assistantTitle = t("redeemCopilot.chat.assistantTitle", {
		tenantName: tenantReadableName,
	});
	const locale = i18n.language as Locale;
	const language = LANGUAGE_NAMES[locale] ?? locale;

	const allDetailsLoaded =
		userLogicalId &&
		email &&
		appName &&
		appSupportEmail &&
		tenantReadableName &&
		authHeaders;

	const initialChatState = useMemo(() => {
		return {
			assistantTitle,
			appName,
			appSupportEmail,
			appTeamName: tenantReadableName,
			userId: userLogicalId,
			username,
			userEmail: email,
			locale,
			language,
		};
	}, [
		assistantTitle,
		appName,
		appSupportEmail,
		tenantReadableName,
		userLogicalId,
		email,
		username,
		locale,
		language,
	]);

	if (isLoading || !isTokenResolved) {
		return <Loader />;
	}

	return (
		<RedeemCopilotContext
			runtimeUrl={COPILOT_URL}
			headers={authHeaders}
			initialChatState={initialChatState}
			instructions={COPILOT_INSTRUCTIONS}
			enableAnalytics
		>
			{allDetailsLoaded && (
				<RedeemCopilotChat
					greetingMessage={t("redeemCopilot.chat.greeting")}
					warningMessage={t("redeemCopilot.chat.betaWarning")}
				/>
			)}
			<RedeemCodeRouter />
		</RedeemCopilotContext>
	);
};

export default RedeemCodeCopilotWrapper;
